import utils from '@eitje/utils'
import {createContext, useContext} from 'react'
import {Association} from '@eitje/api'
import {useShared} from '../../use_shared'
import {useFiltersCache, useClearFilters} from '../hooks'
import {useFilterTaxonomies} from './use_filter_taxonomies'
import {useFilters} from './use_filters'
import {getFirmRecordsAndIds} from './get_firm_records_and_ids'
import {getUserRecordsAndIds} from './get_user_records_and_ids'
import _ from 'lodash'

const FilterContext = createContext({})
const {Provider} = FilterContext

export const FilterProvider = ({children, name, sharedCache, filters, items, rows, fullScreen, layoutProps, pageHeaderItemsCount}) => {
	const {organisation} = useShared()
	name = `${_.snakeCase(sharedCache || name)}_filters`
	filters = utils.alwaysArray(filters)
	const orgCacheKey = organisation.id ? `organisation_${organisation.id}` : undefined

	const filterNames = filters.map(filter => (_.isString(filter) ? filter : filter.name)).map(_.snakeCase)

	// If the cache is shared among multiple filters with the 'sharedCache' prop, only watch the values of the filters that
	// have been assigned to this instance. Otherwise you may falsely get hasFiltered = true and show the clearFilters button.
	const cache = useFiltersCache({name, orgCacheKey})
	const filterValues = _.pickBy(cache, (value, key) => !_.isEmpty(value) && filterNames.includes(key))
	const hasFiltered = !_.isEmpty(filterValues)
	const clearFilters = useClearFilters({orgCacheKey, name})
	const unfilteredItemCount = items.length

	// The taxonomies contain the filter functions, possibly tweaked by props passed to this Filters instance.
	const taxonomies = useFilterTaxonomies({filters, unfilteredItems: items, filtersCacheKey: name, orgCacheKey})
	const itemFilters = taxonomies.filter(t => t.options.filterItems || !t.options.filterRows)
	const rowFilters = taxonomies.filter(t => t.options.filterRows)

	// Some filters are not used to filter items directly, but filter whole rows instead (e.g. hours per member).
	items = useFilters({items, filters: itemFilters, name, orgCacheKey})
	rows = useFilters({items: rows, filters: rowFilters, filteredItems: items, name, orgCacheKey})
	const hasFilteredItems = items.length < unfilteredItemCount

	// This trick's purpose is to show "0 items" in the page header if the 'without_items' filter is active. It should come after
	// filtering the items – otherwise all rows will show since none of them have items, breaking the 'without_items' logic.
	const emptyAllItems = _.isEqual(filterValues?.user_with_filtered_items, ['without_items'])
	if (emptyAllItems) items = new Association([])

	// The currently selected environments and teams are always available in the context.
	const {teams, teamIds, environments, environmentIds} = getFirmRecordsAndIds({taxonomies, name, orgCacheKey})
	const {users, userIds} = getUserRecordsAndIds({taxonomies, name, orgCacheKey})

	const value = {
		// 'items' and 'rows' contain the filtered results, use them to populate your page.
		items,
		rows,

		// Always expose the currently active firms & users. If the filter is not set, this contains all users/
		// firms on the page, just like what you would see in the UI.
		users,
		userIds,
		teams,
		teamIds,
		environments,
		environmentIds,

		// Use below exposed props for usage throughout consumers.
		clearFilters,
		hasFiltered,
		hasFilteredItems,
		filterValues,

		// Styling, only use when you have a diverge from standardised styling variants.
		layoutProps,

		// By default, the page header title counts all items on the page – but sometimes you may want to show a different count.
		pageHeaderItemsCount,

		// Below props are internals you don't have to use while consuming the Filters.
		name,
		orgCacheKey,
		filterTaxonomies: taxonomies,
		fullScreen, // Acts as automated decider, applies correct styling
	}

	return <Provider value={value}>{children}</Provider>
}

export const useFilterContext = () => useContext(FilterContext)
